<template>
    <div id="account-parameters" class="d-flex align-items-stretch">
        <navigation></navigation>
        <div class="container-fluid main-content page-content">
            <div class="page-header">
                <h3 class="page-title">{{$t('workspace-settings')}}</h3>
            </div>
            <div class="page-card text-left">
                <div class="mb-5">
                    <div class="d-flex align-items-end">
                        <div class="tenant-avatar d-flex mr-3">
                            <label for="tenantImage" class="label-avatar mb-0" :title="$t('change')">
                                <span class="sr-only">{{ $t('change') }}</span>
                                <div class="position-relative">
                                    <div class="label-tenant" :style="'--tenant-color:' + tenantColor" v-if="workspaceImage === null">
                                        <model-short-name class="text-center" v-bind:model="tenant"></model-short-name>
                                    </div>
                                    <img class="tenant-img img-fluid" :src="workspaceImage" alt="" v-if="workspaceImage !== null">
                                    <span class="edit-icon" aria-hidden="true">
                                        <font-awesome-icon :icon="['fas', 'pencil-alt']"/>
                                    </span>
                                    <font-awesome-icon class="delete-icon" :icon="['fas', 'times-circle']" @click.prevent="deleteImage()" v-if="workspaceImage !== null"/>
                                </div>
                            </label>
                            <input type="file" class="form-control-file fs-13" accept="image/*" @change="uploadImage()" id="tenantImage" ref="file" hidden>
                        </div>
                        <div class="flex-grow-1">
                            <label for="tenantName" class="text-dark-brown fs-14 font-nunito-SemiBold mb-1">{{ $t('tenant-name') }}</label>
                            <input type="text" id="tenantName" class="form-control" :placeholder="$t('tenant-name')" v-model="workspaceName">
                        </div>
                        <button
                            class="btn btn-primary rounded-pill ml-3"
                            @click.prevent="changeWorkSpaceName()"
                            :disabled="workspaceNameChanged"
                        >
                            {{ $t('save') }}
                        </button>
                    </div>
                </div>
                <div class="mb-4">
                    <p class="text-dark-brown fs-14 font-nunito-SemiBold mb-1">{{ $t('your-plan') }}: {{ userPlan }} {{ userAddons }}</p>
                    <p v-if="user.attributes['is-admin'] || user.attributes['is-super-admin']" class="fs-14 mb-0">
                        {{ $t('payment-info') }}
                        <a href="javascript:void(0)" @click.prevent="openPortalSession">{{ $t('click-here') }}</a>.
                    </p>
                </div>
                <div class="mb-4">
                    <p class="text-dark-brown fs-14 font-nunito-SemiBold mb-1">{{ $t('Conv-automatically-ended') }}</p>
                    <p v-if="user.attributes['is-admin'] || user.attributes['is-super-admin']" class="fs-14 mb-2">
                        {{ $t('desc-Conv-automatically-ended') }}
                    </p>
                    <div class="d-flex align-items-center">
                        <p class="fs-14 m-0">{{ $t('please-select-frequency') }}</p>
                        <div class="col-frequencies col-md-4 col">
                            <select-dropdown
                                :options="frequencies"
                                :selected="selectedFreq"
                                :type="'name'"
                                :isDisabled="loading || getArchiveConversationParameter == null"
                                v-on:selectOption="changeFrequency($event)">
                            </select-dropdown>
                        </div>
                    </div>
                </div>
                <div class="mb-4">
                    <p class="text-dark-brown fs-14 font-nunito-SemiBold mb-1">{{ $t('inactivity-session') }}</p>
                    <p v-if="user.attributes['is-admin'] || user.attributes['is-super-admin']" class="fs-14 mb-2">
                        {{ $t('inactivity-session-desc') }}
                    </p>
                    <div class="d-flex align-items-center">
                        <p class="fs-14 m-0">{{ $t('inactivity-session-dropdown') }}</p>
                        <div class="col-frequencies col-md-4 col">
                            <select-dropdown
                                :options="inactivityDelays"
                                :selected="selectedDelay"
                                :type="'name'"
                                :isDisabled="loading"
                                v-on:selectOption="changeDelay($event)">
                            </select-dropdown>
                        </div>
                    </div>
                </div>
                <div class="footer">
                    <div class="footer-info">
                        <p class="text-center fs-12 pb-0 mb-0">{{ $t('more-options') }}</p>
                        <p class="text-center fs-12">
                            <router-link :to="{ name: `cocoapp-wordpress-packages:${this.$i18n.locale}` }" target="_blank">
                                {{ $t('consult-plans') }}
                            </router-link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>

// Internal components
import ModelShortName      from './ModelShortName.vue';
import chargbeePortalMixin from '@/core/mixins/chargbeePortalMixin';
import SelectDropdown      from '@/core/components/SelectDropdown.vue';

// Internal services js
import Alert        from '@/helpers/Alert.js';
import { EventBus } from '@/helpers/EventBus.js';

// Import external libraries
import {
    mapState,
    mapActions,
    mapGetters
} from 'vuex';

export default {
    components: { ModelShortName, SelectDropdown },

    mixins: [chargbeePortalMixin],

    data () {
        return {
            updateTenants:  false,
            tenantColor:    '',
            workspaceName:  '',
            workspaceImage: null,
            loading:        false
        };
    },

    computed: {
        // State with the user
        ...mapState('core/auth', [
            'user'
        ]),

        // State with the tenant
        ...mapState('core/tenants', { tenant: 'selectedTenant' }),

        /**
         * Decide when submit button must be disabled
         * Submit button must be disabled when workspace name does not change or is empty.
         */
        workspaceNameChanged () {
            return this.workspaceName === this.tenant.attributes.name || this.workspaceName === '';
        },

        /**
         * Return the user's plan based on the remote plan id
         */
        userPlan () {
            return this.$i18n.t(this.tenant.attributes['remote-plan-id']);
        },

        /**
         * Return the user addons based on the remote plan addons
         */
        userAddons () {
            if (this.tenant && this.tenant.attributes['remote-plan-addons'] !== null) {
                const addonsArray = this.tenant.attributes['remote-plan-addons'].map((item) => this.$i18n.t(`addon-${item.id}`));
                const addonsString = addonsArray.join(', ');
                return `(${addonsString})`;
            }
            return '';
        },

        ...mapGetters('core/parameters', ['getArchiveConversationParameter', 'getInactivitySessionParameter']),

        selectedFreq () {
            const selectedConfig = this.getArchiveConversationParameter ? this.getArchiveConversationParameter.attributes.config : null;
            return selectedConfig?.value ? `${selectedConfig.value} ${this.$i18n.tc(selectedConfig.unit, selectedConfig.value)}` : this.$i18n.t('never');
        },

        frequencies () {
            const options = [];
            if (this.getArchiveConversationParameter && this.getArchiveConversationParameter.attributes.options) {
                this.getArchiveConversationParameter.attributes.options.forEach(option => {
                    options.push({
                        ...option,
                        name: option.value ? `${option.value} ${this.$i18n.tc(option.unit, option.value)}` : this.$i18n.t('never')
                    });
                });
            }

            return options;
        },

        inactivityDelays () {
            const options = [];
            if (this.getInactivitySessionParameter && this.getInactivitySessionParameter.attributes.options) {
                this.getInactivitySessionParameter.attributes.options.forEach(option => {
                    // eslint-disable-next-line no-magic-numbers
                    const hours = option.value ? option.value / 60 : null;

                    options.push({
                        ...option,
                        name: hours
                            ? `${hours} ${this.$i18n.tc('hour', hours)}`
                            : this.$i18n.t('never')
                    });
                });
            }

            return options;
        },

        selectedDelay () {
            const selectedConfig = this.getInactivitySessionParameter
                ? this.getInactivitySessionParameter.attributes.config
                : null;

            // eslint-disable-next-line no-magic-numbers
            const hours = selectedConfig?.value ? selectedConfig.value / 60 : null;

            return hours
                ? `${hours} ${this.$i18n.tc('hour', hours)}`
                : this.$i18n.t('never');
        }
    },

    mounted () {
        // Set color for tenant when no image represent the tenant avatar
        this.tenantColor = this.tenant.attributes['default-color'] || '#FFDEEB';

        this.workspaceName = this.tenant.attributes.name;
        this.workspaceImage = this.tenant.attributes['profile-picture'];

        this.getTenantParameters();
    },

    methods: {
        /**
         * Methods for Tenants API request
         *
         * updloadTenantPicture     API request to upload avatar image for tenant
         * deleteTenantPicture      API request to remove avatar image for tenant
         */
        ...mapActions('core/tenants', [
            'updloadTenantPicture',
            'deleteTenantPicture',
            'update'
        ]),

        ...mapActions('core/parameters', { 'getParameters': 'index', 'updateParameter': 'update' }),

        /**
         * Get the tenant parameters
         */
        getTenantParameters () {
            this.loading = true;
            this.getParameters()
                .finally(() => {
                    this.loading = false;
                });
        },

        /**
         * Upload image for tenant avatar
         */
        uploadImage () {
            if (!this.$refs.file.files[0]) {
                return;
            }

            this.updloadTenantPicture({
                id:    this.tenant.id,
                image: this.$refs.file.files[0]
            })
                .then(() => {
                    // Using timestamp (Date.now()) to update the new image.
                    this.workspaceImage = `${this.tenant.attributes['profile-picture']}?${Date.now()}`;
                    Alert.success(this.$i18n.t('workspace-image-modified'));

                    EventBus.$emit('tenant-image-updated');
                })
                .catch(() => {
                    Alert.fail(this.$i18n.t('default-error'));
                });
        },

        /**
         * Remove image for tenant avatar
         */
        deleteImage () {
            this.$dialog.confirm({
                text:        this.$t('confirm-delete-picture'),
                centerText:  true,
                confirmText: this.$t('delete'),
                danger:      true,
                onConfirm:   () => this.deleteTenantPicture({ id: this.tenant.id })
            })
                .then((confirmed) => {
                    if (!confirmed) {
                        return;
                    }

                    this.workspaceImage = null;
                    Alert.success(this.$i18n.t('workspace-image-deleted'));
                    EventBus.$emit('tenant-image-updated');
                })
                .catch(() => {
                    Alert.fail(this.$i18n.t('default-error'));
                });
        },

        /**
         * Update workspace name
         */
        changeWorkSpaceName () {
            this.update({
                id:   this.tenant.id,
                name: this.workspaceName
            })
                .then(() => {
                    Alert.success(this.$i18n.t('workspace-name-modified'));
                })
                .catch(() => {
                    Alert.fail(this.$i18n.t('default-error'));
                });
        },

        /**
         * Change language in dropdown select.
         */
        changeFrequency (option) {
            const attributes = {
                'config': {
                    'unit':  option.unit,
                    'value': option.value
                }
            };

            this.updateParameter({
                id: this.getArchiveConversationParameter.id,
                attributes
            })
                .then(() => Alert.success(this.$i18n.t('conv-automatically-ended-modified')))
                .catch(() => Alert.fail(this.$i18n.t('default-error')));
        },

        changeDelay (option) {
            const attributes = {
                'config': {
                    'unit':  option.unit,
                    'value': option.value
                }
            };

            this.updateParameter({
                id: this.getInactivitySessionParameter.id,
                attributes
            })
                .then(() => Alert.success(this.$i18n.t('inactivity-session-modified')))
                .catch(() => Alert.fail(this.$i18n.t('default-error')));
        }
    }
};
</script>
